import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { loadImageLookupsSnapshot, saveImageLookupsSnapshot, type CachedLookupRow } from "@/lib/image-lookups-cache";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";

interface LookupSelectProps {
  type: "brand" | "color" | "prelis" | "panel_structure" | "folder_type" | "model";
  value: string | undefined;
  onValueChange: (value: string | undefined) => void;
  label: string;
  placeholder?: string;
  id?: string;
}

export default function LookupSelect({
  type,
  value,
  onValueChange,
  label,
  placeholder,
  id,
}: LookupSelectProps) {
  const { t } = useTranslation();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newValue, setNewValue] = useState("");
  const qc = useQueryClient();

  // Fetch lookups (cache last success for offline: Felület / Szín / Prelis in measurement)
  const lookupsQuery = useQuery({
    queryKey: ["image-lookups", type],
    queryFn: async () => {
      try {
        const r = await fetch(apiUrl(`/api/images/lookups?type=${type}`), {
          credentials: "include",
        });
        if (!r.ok) {
          if (r.status === 500) {
            console.warn("[LookupSelect] Table might not exist, trying cache");
            return loadImageLookupsSnapshot(type) ?? [];
          }
          throw new Error(t("lookupSelect.loadFailed"));
        }
        const data = await r.json();
        const lookups = (data.lookups || []) as CachedLookupRow[];
        saveImageLookupsSnapshot(type, lookups);
        return lookups;
      } catch {
        return loadImageLookupsSnapshot(type) ?? [];
      }
    },
    retry: false, // Don't retry if table doesn't exist
  });

  // Create lookup mutation
  const createLookup = useMutation({
    mutationFn: async (value: string) => {
      const r = await fetch(apiUrl("/api/images/lookups"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ type, value }),
      });
      if (!r.ok) {
        const error = await r.json();
        // If table doesn't exist, show helpful message
        if (error.error === "table_not_found" || r.status === 404) {
          throw new Error(t("lookupSelect.tableNotExist"));
        }
        throw new Error(error.message || t("lookupSelect.addFailed"));
      }
      return r.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["image-lookups", type] });
      setShowAddDialog(false);
      setNewValue("");
      // Automatically select the newly created value
      onValueChange(data.lookup.value);
    },
    onError: (error: Error) => {
      alert(`${t("common.error")}: ${error.message}`);
    },
  });

  const handleAdd = () => {
    if (!newValue.trim()) {
      alert(t("lookupSelect.enterValueRequired"));
      return;
    }
    createLookup.mutate(newValue.trim());
  };

  const lookups = lookupsQuery.data || [];

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <div className="flex gap-2">
          <Select value={value || ""} onValueChange={(val) => onValueChange(val || undefined)}>
            <SelectTrigger id={id} className="flex-1">
              <SelectValue placeholder={placeholder ?? t("lookupSelect.choosePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {lookups.map((lookup) => (
                <SelectItem key={lookup.id} value={lookup.value}>
                  {lookup.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setShowAddDialog(true)}
            title={t("lookupSelect.addNewTitle")}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Új {label.toLowerCase()} hozzáadása</DialogTitle>
            <DialogDescription>
              {t("lookupSelect.enterNewValueName", { label: label.toLowerCase() })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-value">Név</Label>
              <Input
                id="new-value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={`pl. ${type === "brand" ? "Hörmann" : type === "color" ? "RAL 9006" : "M"}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAdd();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleAdd} disabled={createLookup.isPending || !newValue.trim()}>
              {createLookup.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Hozzáadás...
                </>
              ) : (
                "Hozzáadás"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
